import { Injectable } from '@angular/core';
import { ColDef, ColumnState } from 'ag-grid-community';
import { CVApiService } from 'src/app/reusables/services/cv-api.service';
import { ApiResponse } from 'src/shared/models/api-response.model';
import { ReportingSetData, ReportingSetDefinitionApiModel } from 'src/shared/models/pipeline-config/reporting-sets.models';

@Injectable({
    providedIn: 'root'
})

export class ReportService {
    public lastDefinitionUpdate = Date.now();

    constructor(
        private service: CVApiService,
    ) { }

    async loadReportDefinitions(moduleID: number): Promise<ReportingSetDefinitionApiModel[]> {
        const url = `/api/v2/pipeline/report-definitions/${moduleID}`;
        try {
            const response = await this.service.get<ReportingSetDefinitionApiModel[]>(url);
            if (response.status_code >= 200 && response.status_code < 300) {
                this.lastDefinitionUpdate = Date.now();
                return response.data;
            }
            else throw response;
        } catch (error) {
            console.error('Failed getting report definitions');
            throw error;
        }
    }

    async loadData(
        reportName: string,
        dateField: string,
        moduleKey: string,
    ): Promise<ReportingSetData> {
        if (reportName === undefined)
            return;

        let renderEndpoint = await this.service.getModuleRenderEndPoint(moduleKey);
        try {
            renderEndpoint = await this.service.getModuleRenderEndPoint(moduleKey);
        } catch (error) {
            console.error('Failed getting render endpoint.');
            throw error;
        }

        try {
            const url = `/api/v2/pipeline/report/${reportName}/${moduleKey}/data?dateField=${dateField}`;
            const response = await this.service.get<ReportingSetData>(url, renderEndpoint);

            if (response.status_code !== 200)
                throw response;
            else
                return response.data;
        } catch (error) {
            console.error('Failed getting report.');
            throw error;
        }
    }

    async exportData(
        reportName: string,
        dateField: string,
        moduleKey: string,
        format: string = 'csv',
    ): Promise<ApiResponse<string>> {
        if (reportName === undefined)
            return;

        let renderEndpoint = await this.service.getModuleRenderEndPoint(moduleKey);
        try {
            renderEndpoint = await this.service.getModuleRenderEndPoint(moduleKey);
        } catch (error) {
            console.error('Failed getting render endpoint.');
            throw error;
        }

        try {
            const url = `/api/v2/pipeline/report/${reportName}/${dateField}/${moduleKey}/export/${format}`;
            const response = await this.service.get<string>(url, renderEndpoint);

            if (response.status_code !== 200)
                throw response;
            else
                return response;
        } catch (error) {
            console.error('Failed exporting report.')
            throw error;
        }
    }

    upgradeColumnStates(colDefs: ColDef[], colState: ColumnState[]) {
        const columnsInData = new Set(colDefs.map(cd => cd.colId));
        const columnsNotInData = colState.filter(col => !columnsInData.has(col.colId) && col.colId !== 'ag-Grid-AutoColumn');
        if (columnsNotInData.length === 0)
            return false; // no conversion

        // convert missing states where possible
        for (const columnState of columnsNotInData) {
            if (columnsInData.has(`Date_${columnState.colId}`))
                columnState.colId = `Date_${columnState.colId}`;
            else if (columnsInData.has(`EmpHierarchy_${columnState.colId}`))
                columnState.colId = `EmpHierarchy_${columnState.colId}`;
            else if (columnsInData.has(`ContactHierarchy_${columnState.colId}`))
                columnState.colId = `ContactHierarchy_${columnState.colId}`;
        }
        return true;
    }

}
